import axios from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'
import { correctionalFacilityId } from '@fs/zion-config'
import { CORRECTIONS_REVIEWED, INDEXED_INCORRECTLY } from '@fs/zion-sls'
import { VOLUNTEER_BASE_URL, headers } from '../config'
import { errorCollector, getNextUuid } from '../../utils/utils'
import updateLastSearchStats from './updateLastSearchStats'
import updateSessionStats from './updateSessionStats'
import { getBadActorFlag } from './badActorStore'
import saveInterceptor from './saveInterceptor'

// Resource: https://sg32i0.familysearch.org/service/records/volunteer/orchestration/apidocs/resource_RecordEnhancementTaskController.html

const debug = zionDebug('rip:record-enhancement-service')
const MAX_REQUESTS = 500

/**
 * Request the static list of available countries names with their code.
 * @returns {Array} array of objects in this format:
 * {code: "164", name: "Argentina"}
 */
async function getCountries() {
  const url = `${VOLUNTEER_BASE_URL}/record-enhancement/country`
  const response = await axios.get(url, { headers })
  const { data } = response
  return data
}
/**
 * Request the list of available languages with their code.
 * @returns {Object} Array} array of objects in this format:
 * {"name": "Español", "code": "es"}
 */
async function getLanguages() {
  const url = `${VOLUNTEER_BASE_URL}/record-enhancement/language`
  const response = await axios.get(url, { headers })
  const { data } = response
  return data
}

/**
 * Request/reserve a single record enhancement task id
 * @returns {Object} data in usable format
 */
async function reserveTask(language = '', placeRepId = 0, taskType = '', surname = '', givenName = '') {
  const url = `${VOLUNTEER_BASE_URL}/record-enhancement/task`
  const payload = { language, placeRepId, task: taskType, surname, givenName }
  if (taskType === '1950 Census Household Correction' || taskType === '1950 Census Header Correction') {
    // This prop limits us to artifact search, which has the bait and switch fix (Gives you a different state than you requested)
    payload.recordTypeConceptId = 123363
  }
  return axios.post(url, payload, { headers })
}

/**
 * Get already reserved task
 * @returns {Object} data in usable format
 */
async function getCurrentTask(action, taskType, surname) {
  const startValueParam = surname ? `&startValue=${surname}` : ''
  const url = `${VOLUNTEER_BASE_URL}/record-enhancement/task/current?action=${action}${startValueParam}&task=${encodeURIComponent(
    taskType
  )}`
  return axios.get(url, { headers })
}

/**
 * Request the current status of an async task request
 * @returns {Object} data in usable format
 */
async function requestTaskStatus(locationHeader = '') {
  const url = `${VOLUNTEER_BASE_URL}/record-enhancement/task/${locationHeader}`
  return axios.get(url, { headers })
}

/**
 * Checks to see if we have a task and returns the task data
 * @param {Object} data response data from tryForTask calls
 * @param {Object} currentHeaders Headers from response
 * @param {Boolean} isFirstRequest first request or not
 * @returns {Object} task data
 */
async function checkTask(data, currentHeaders, isFirstRequest) {
  const retryTime = parseInt(currentHeaders.retry, 10)
  // sometimes the task retrieved is already reserved and ready to go
  if (data.taskData) return data.taskData
  for (let i = 0; i < MAX_REQUESTS; i++) {
    /* eslint-disable no-await-in-loop */
    const response = await requestTaskStatus(data.requestId, isFirstRequest)
    if (response?.status === 200) {
      return response.data.taskData
    }
    // 204 means no content, exit loop and then try again with new task id
    if (response?.status === 204) return null
    // this means the task is pending, wait designated time then try again with same task id
    setTimeout(() => {}, retryTime)
  }
  throw new Error('NoMoreTasks')
}

/**
 * Reserve and retrieve a new task. Data comes asychronously and must be checked.
 * If it is the first request, use the task endpoint, if not, use the done endpoint.
 * First call reserveTask will get either a pending task or an already reserved
 * task. If it is pending, repeatedly call requestTaskStatus until it returns either 200 or 204.
 * If it returns 204, try reserving a different task.
 * Use MAX_REQUESTS to manage how many times the requests are made.
 * @returns {Object} data in usable format
 */
async function tryForTask(language, placeRepId, isFirstRequest = false, apid = '', taskType = null) {
  let response
  try {
    if (isFirstRequest || !apid) {
      const { data, headers: reserveHeaders } = await reserveTask(language, placeRepId, taskType)
      response = await checkTask(data, reserveHeaders, true)
    } else {
      const { data, headers: completeHeaders } = await taskCompleted(apid, true, taskType)
      response = await checkTask(data, completeHeaders, false)
    }
    return response
  } catch (err) {
    // We get a lot of 404s checking task status.  Chris Nelson "The Redis object has expired after 5 minutes"
    // A 404 doesn't make a lot of sense.  It is not missing, it just has timed out.  We don't need to report this to Sentry
    if (err?.status === 404) throw new Error('TaskExpired')
    else if (/NoMoreTasks/.test(err.message)) throw new Error('NoMoreTasks')
    else {
      errorCollector(err, apid, {
        codeId: 668,
        status: err?.response?.status,
        responseBody: err?.response?.data,
      })
      throw new Error(err)
    }
  }
}

/**
 * Try for task. If the response is a 204, try again until you get it or you
 * try more than the max requests.
 * @returns {Object} task list or null
 */
async function getTaskList({ placeCode, language, isFirstRequest, apid, action, taskType, surname }) {
  let task = null
  // console.log('getTaskList: ', { placeCode, language, isFirstRequest, apid, action, taskType, surname })
  if (isFirstRequest) {
    const response = await getCurrentTask(action, taskType, surname)
    if (response?.status === 204) throw new Error('NoMoreTasks')
    task = response?.data
  } else task = await tryForTask(language, placeCode, isFirstRequest, apid, taskType)
  if (!task) throw new Error('NoMoreTasks')
  if (task.slsImageInfo) {
    // console.log('HErE: ', task.slsImageInfo)
    if (typeof task.slsImageInfo === 'string') {
      task.slsImageInfo = JSON.parse(task.slsImageInfo)
    }
  }
  return task
}

/**
 * Tell the backend that the tasks on this image are complete.
 * They will unreserve the tasks check them for missing tasks
 * Possibly request/reserve another record enhancement task
 * requestNext param will return a new reserved task locationHeader if true
 * @param apid - the image apid
 * @param requestNext - whether or not to make a new request
 * @returns {Object} data in usable format
 */
async function taskCompleted(apid, requestNext = false, taskType = null) {
  if (apid) {
    const url = `${VOLUNTEER_BASE_URL}/record-enhancement/apid/${apid}/done?requestNext=${requestNext}&task=${encodeURIComponent(
      taskType
    )}`
    const response = await axios.post(url, {}, { headers })
    if (requestNext && response?.status === 204) throw new Error('NoMoreTasks')
    return response
  }
  return null
}

function addFieldValue(partitionKey, id, elementType, edit, isFixed) {
  const reasonId = isFixed ? INDEXED_INCORRECTLY : CORRECTIONS_REVIEWED
  return {
    partitionKey,
    id,
    elementType,
    opCode: 'ADD_FV',
    fieldValueArg: {
      id: getNextUuid(),
      origValue: {
        text: edit,
        attribution: {
          comment: '',
          reasonId,
          app: 'NAME_WEB',
        },
      },
    },
  }
}

function addNewFieldType(partitionKey, id, newFieldType) {
  return { partitionKey, id, elementType: 'FIELD', opCode: 'SET_FIELD_TYPE', fieldTypeArg: newFieldType }
}

// Deprecated
const saveNotAName = (partitionKey, fieldIds, taskType) => {
  const actions = []
  fieldIds.forEach((id) => {
    actions.push(addNewFieldType(partitionKey, id, 'WORD'))
  })
  axios.post(`${VOLUNTEER_BASE_URL}/record-enhancement/updates?task=${encodeURIComponent(taskType)}`, actions, {
    headers,
  })
}

/**
 * Submit user corrections to the record enhancement orchestration level before sending it off to SLS.
 * This will ensure tracking for volunteer inventory.
 * Name Corrections
 * @param partitionKey - the sls key for that image, usually a 3:1... ark
 * @param id - the sls guid for that specific field
 * @param edit - the edit made by the user
 * @param isFinished - whether or not the field has been declared a match or fixed
 * @param requestNext - whether or not the next task should be sent
 * @param isFixed - did the user make a fix (true), or was it a match (false)
 * @returns {Object} data in usable format
 */
const saveEdit = async ({
  partitionKey,
  id,
  edit,
  fieldValues,
  isFixed,
  newFieldType,
  isFinished = false,
  requestNext = false,
  placeRepId,
  // eslint-disable-next-line consistent-return
}) => {
  const badActorFlag = await getBadActorFlag()
  debug(`saveEdit:badActorFlag`, badActorFlag)
  if (fieldValues?.[0]) fieldValues[0].editedValue = edit
  const intercepted =
    isFixed && badActorFlag.isOn && !!correctionalFacilityId ? await saveInterceptor(fieldValues) : false
  if (!intercepted) {
    const fieldValue = []
    if (newFieldType) fieldValue.push(addNewFieldType(partitionKey, id, newFieldType))
    if (newFieldType !== 'WORD') fieldValue.push(addFieldValue(partitionKey, id, 'FIELD', edit, isFixed))
    // we do not submit the original field value that added for use by the interceptors.
    debug(`saveEdit:fieldValue:payload`, fieldValue)
    if (placeRepId) {
      axios.post(`/service/volunteer/edge/widget-exp/involvement/project/${placeRepId}`)
    }
    return axios.post(
      `${VOLUNTEER_BASE_URL}/record-enhancement/updates?isFinished=${isFinished}&requestNext=${requestNext}`,
      fieldValue,
      { headers }
    )
  }
}

// eslint-disable-next-line consistent-return
const saveTaskEdits = async (partitionKey, slsPayload, isFinished = true, requestNext = false, taskType = null) => {
  debug(`slsPayload`, slsPayload)
  const badActorFlag = await getBadActorFlag()
  debug(`badActorFlag`, badActorFlag)
  const intercepted = badActorFlag.isOn && !!correctionalFacilityId ? await saveInterceptor(slsPayload) : false
  if (!intercepted) {
    // we need to strip originalValue from the payload before sending it to the backend
    // because is is not part of the SLS spec.
    const strippedSlsPayload = slsPayload.map((item) => {
      if (item.elementType === 'FIELD') {
        delete item.originalValue
      }
      return item
    })
    debug('strippedSlsPayload', strippedSlsPayload)
    return axios.post(
      `${VOLUNTEER_BASE_URL}/record-enhancement/updates?isFinished=${isFinished}&requestNext=${requestNext}&task=${encodeURIComponent(
        taskType
      )}`,
      strippedSlsPayload,
      { headers }
    )
  }
}

/**
 * Sends a message to the statistics service reporting that the current patron completed a task
 * As of now, we are only sending this when a 1950 Household Correction or 1950 Header correction
 * task has been completed (that means an entire image)
 * @param partitionKey - the sls key for that image, usually a 3:1... ark
 * @param taskType - '1950 Census Household Correction' or '1950 Census Header Correction'
 */
const patronCompletedTaskStats = (partitionKey, taskType) =>
  axios.post(
    `${VOLUNTEER_BASE_URL}/record-enhancement/stats/task/${encodeURIComponent(taskType)}/ark/${partitionKey}`,
    {},
    { headers }
  )

/**
 * Tracks user edits/reviews for the GIVE project
 * @param {Object} parameters - The object containing the parameters
 * @param {string} parameters.partitionKey - the sls key for that image, usually a 3:1... ark (required)
 * @param {string} parameters.taskType - 'Name Correction', 'Name Review', or 'Family Review' (required)
 * @param {string} parameters.action - 'edited' or 'reviewed' (required)
 */
function saveGetInvolvedStats({ partitionKey, taskType, action }) {
  if (!partitionKey || !taskType || !action) throw new Error('Missing required parameters')
  if (action !== 'fixed' && action !== 'reviewed' && action !== 'not-a-person')
    throw new Error('Invalid action type. Valid types are "fixed", "not-a-person" or "reviewed"')
  if (taskType !== 'Family Review' && taskType !== 'Full Name Review' && taskType !== 'Name Correction')
    throw new Error('Invalid task type. Valid types are "Name Correction", "Name Review", or "Family Review"')

  if (action === 'fixed' || action === 'reviewed' || action === 'not-a-person') {
    updateLastSearchStats(taskType)
    updateSessionStats(taskType)
  }

  // We need to change this once Orchstration stops saving stats
  if (taskType === 'Name Correction') return

  axios.post(
    `${VOLUNTEER_BASE_URL}/record-enhancement/stats`,
    {
      action,
      artifactARK: partitionKey,
      task: taskType,
      count: 1,
    },
    { headers }
  )
}

export {
  checkTask,
  getCountries,
  getLanguages,
  reserveTask,
  getCurrentTask,
  requestTaskStatus,
  getTaskList,
  patronCompletedTaskStats,
  taskCompleted,
  saveEdit,
  saveTaskEdits,
  saveNotAName,
  saveGetInvolvedStats,
}
